import React, { useState, useMemo } from "react";
import {
  FiEdit3, FiRefreshCw, FiDownload, FiTrash2, FiMoreVertical,
  FiZap, FiWifi, FiServer, FiRadio, FiCpu, FiActivity,
  FiAlertTriangle, FiCheckCircle, FiClock, FiMapPin,
  FiToggleLeft, FiToggleRight, FiSave, FiX,
  FiChevronRight, FiShield, FiPackage,
} from "react-icons/fi";
import { MdSettingsInputAntenna } from "react-icons/md";
import {
  statusMeta, connMeta, roleMeta, healthColor, healthLabel,
  HealthRing, BatteryBar, SignalBars, DvcBadge,
} from "./deviceHelpers";
import { allDevices } from "../../../Data/DeviceData";

// ── Seeded trend data (deterministic) ────────────────────────────────────────
const seededRnd = (seed, i) => { const x = Math.sin(seed * 9301 + i * 49297 + 233) * 10000; return x - Math.floor(x); };
const genTrend = (base, seed, hours = 24) =>
  Array.from({ length: hours }, (_, i) => ({
    t: `${String(i).padStart(2, "0")}:00`,
    val: base > 0 ? +(base * (0.85 + seededRnd(seed, i) * 0.3)).toFixed(1) : 0,
  }));

// ── Telemetry field definitions per device type ───────────────────────────────
const TELE_MAP = {
  "Water Level": [
    { key: "level",     label: "Water Level",   unit: "m",     warnHi: 8,    color: "#3b82f6" },
    { key: "flow",      label: "Flow Rate",     unit: "m³/h",  warnHi: 30,   color: "#10b981" },
    { key: "turbidity", label: "Turbidity",     unit: "NTU",   warnHi: 10,   color: "#f59e0b" },
    { key: "ph",        label: "pH",            unit: "",      warnLo: 6.5, warnHi: 9, color: "#8b5cf6" },
    { key: "temp",      label: "Temperature",   unit: "°C",    warnHi: 35,   color: "#f97316" },
  ],
  "AQM Sensor": [
    { key: "pm25",     label: "PM2.5",         unit: "µg/m³", warnHi: 60,   color: "#ef4444" },
    { key: "co2",      label: "CO₂",           unit: "ppm",   warnHi: 1000, color: "#8b5cf6" },
    { key: "tvoc",     label: "TVOC",          unit: "mg/m³", warnHi: 0.5,  color: "#f59e0b" },
    { key: "temp",     label: "Temperature",   unit: "°C",    warnHi: 35,   color: "#f97316" },
    { key: "humidity", label: "Humidity",      unit: "%",     warnHi: 80,   color: "#3b82f6" },
  ],
  "pH Sensor": [
    { key: "ph",           label: "pH",            unit: "",      warnLo: 6.5, warnHi: 9, color: "#8b5cf6" },
    { key: "orp",          label: "ORP",           unit: "mV",                             color: "#10b981" },
    { key: "conductivity", label: "Conductivity",  unit: "µS/cm",                          color: "#3b82f6" },
    { key: "dissolved_o2", label: "Dissolved O₂",  unit: "mg/L",  warnLo: 5,               color: "#06b6d4" },
    { key: "temp",         label: "Temperature",   unit: "°C",    warnHi: 35,               color: "#f97316" },
  ],
  "Energy Meter": [
    { key: "voltage", label: "Voltage",      unit: "V",    color: "#f59e0b" },
    { key: "current", label: "Current",      unit: "A",    color: "#ef4444" },
    { key: "power",   label: "Active Power", unit: "kW",   color: "#8b5cf6" },
    { key: "energy",  label: "Total Energy", unit: "kWh",  color: "#3b82f6" },
    { key: "pf",      label: "Power Factor", unit: "",     warnLo: 0.85, color: "#10b981" },
    { key: "freq",    label: "Frequency",    unit: "Hz",   color: "#6b7280" },
  ],
  "Flow Meter": [
    { key: "flow",        label: "Flow Rate",     unit: "m³/h", color: "#3b82f6" },
    { key: "totalVolume", label: "Total Volume",  unit: "m³",   color: "#10b981" },
    { key: "pressure",    label: "Pressure",      unit: "bar",  warnHi: 5, color: "#f59e0b" },
    { key: "temp",        label: "Temperature",   unit: "°C",   color: "#f97316" },
  ],
  "CO2 Sensor": [
    { key: "co2",      label: "CO₂",         unit: "ppm", warnHi: 1000, color: "#8b5cf6" },
    { key: "temp",     label: "Temperature", unit: "°C",                 color: "#f97316" },
    { key: "humidity", label: "Humidity",    unit: "%",                  color: "#3b82f6" },
    { key: "tvoc",     label: "TVOC",        unit: "mg/m³",              color: "#f59e0b" },
  ],
  "Soil Moisture": [
    { key: "moisture", label: "Soil Moisture", unit: "%",  warnLo: 20, color: "#10b981" },
    { key: "temp",     label: "Soil Temp",     unit: "°C",              color: "#f97316" },
  ],
  "Temperature": [
    { key: "temp",     label: "Temperature", unit: "°C", color: "#f97316" },
    { key: "humidity", label: "Humidity",    unit: "%",  color: "#3b82f6" },
    { key: "pressure", label: "Pressure",    unit: "hPa", color: "#6b7280" },
  ],
};

// ── Mini Trend Chart ──────────────────────────────────────────────────────────
const TrendChart = ({ device, timeRange }) => {
  const hours = timeRange === "1H" ? 1 : timeRange === "6H" ? 6 : timeRange === "7D" ? 168 : 24;
  const displayPoints = Math.min(hours, 24);
  const fields = TELE_MAP[device.type] || [];
  const primaryKey = fields[0]?.key || Object.keys(device.telemetry || {})[0];
  const baseVal = device.telemetry?.[primaryKey] || 50;
  const seed = device.id.charCodeAt(0) + device.id.charCodeAt(device.id.length - 1);
  const trend = useMemo(() => genTrend(baseVal, seed, displayPoints), [baseVal, seed, displayPoints]);
  const maxVal = Math.max(...trend.map(p => p.val), 1);
  const col = fields[0]?.color || "#667eea";

  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 3, height: 80, padding: "0 4px" }}>
      {trend.map((pt, i) => (
        <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
          <div style={{ width: "100%", height: `${(pt.val / maxVal) * 72}px`, background: col, borderRadius: "3px 3px 0 0", opacity: 0.8, minHeight: 2 }} />
          {displayPoints <= 12 && <div style={{ fontSize: 8, color: "#9ca3af" }}>{pt.t.split(":")[0]}</div>}
        </div>
      ))}
    </div>
  );
};

// ── 1. Device Identity Header ─────────────────────────────────────────────────
const DeviceIdentityBar = ({ device, onEdit, onReboot, onFirmware, onDelete }) => {
  const [showMore, setShowMore] = useState(false);
  const sm = statusMeta(device.status);
  const cm = connMeta(device.connectivity);
  const rm = roleMeta(device.role);
  const hc = healthColor(device.healthScore);

  return (
    <div className="dvc-id-bar">
      <div className="dvc-id-bar-left">
        <div className="dvc-id-role-badge" style={{ background: rm.color }}>{rm.letter}</div>
        <div>
          <div className="dvc-id-name">{device.name}</div>
          <div className="dvc-id-meta">
            <span>{device.id}</span><span>·</span>
            <span>{device.type}</span><span>·</span>
            <span>{device.model}</span><span>·</span>
            <span>{device.firmware}</span>
            {device.site && <><span>·</span><FiMapPin size={11} /><span>{device.site}</span></>}
          </div>
          <div className="dvc-id-badges">
            <span className="dvc-id-badge" style={{ background: sm.bg, color: sm.color }}>
              <sm.icon size={11} /> {device.status}
            </span>
            <span className="dvc-id-badge" style={{ background: hc + "18", color: hc }}>
              ❤ {device.healthScore} — {healthLabel(device.healthScore)}
            </span>
            <span className="dvc-id-badge" style={{ background: cm.bg, color: cm.color }}>
              <cm.Icon size={11} /> {device.connectivity}
            </span>
            {device.battery !== null && device.battery !== undefined && (
              <span className="dvc-id-badge" style={{ background: device.battery < 20 ? "#fee2e2" : "#f0fdf4", color: device.battery < 20 ? "#ef4444" : "#10b981" }}>
                🔋 {device.battery}%
              </span>
            )}
            {device.signal && (
              <span className="dvc-id-badge" style={{ background: "#f0f9ff", color: "#0369a1" }}>
                📡 {device.signal} dBm
              </span>
            )}
            <span className="dvc-id-badge" style={{ background: "#f8fafc", color: "#6b7280" }}>
              <FiClock size={11} /> {device.lastSeen}
            </span>
          </div>
        </div>
      </div>
      <div className="dvc-id-actions">
        <button className="dvc-ctrl-btn dvc-ctrl-blue" onClick={onEdit}><FiEdit3 size={13} /> Edit</button>
        <button className="dvc-ctrl-btn dvc-ctrl-orange" onClick={onReboot}><FiRefreshCw size={13} /> Reboot</button>
        <button className="dvc-ctrl-btn dvc-ctrl-purple" onClick={onFirmware}><FiDownload size={13} /> Firmware</button>
        <button className="dvc-ctrl-btn dvc-ctrl-red" onClick={onDelete}><FiTrash2 size={13} /> Delete</button>
        <div style={{ position: "relative" }}>
          <button className="dvc-ctrl-btn" style={{ background: "#f1f5f9", color: "#374151" }} onClick={() => setShowMore(x => !x)}>
            <FiMoreVertical size={15} />
          </button>
          {showMore && (
            <div className="dvc-more-menu">
              {["View History", "Duplicate", "Export Config", "Move to Site", "Run Diagnostic"].map(a => (
                <div key={a} className="dvc-more-item" onClick={() => setShowMore(false)}>{a}</div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── 2. Overview Tab ───────────────────────────────────────────────────────────
const OverviewTab = ({ device }) => {
  const [timeRange, setTimeRange] = useState("24H");
  const fields = TELE_MAP[device.type] || [];
  const tele = device.telemetry || {};

  const packets24h = device.loraDetails?.packetsToday || (device.status === "Online" ? 288 : 12);
  const pktLoss    = device.loraDetails?.packetLoss    || (device.status === "Warning" ? 6.2 : device.status === "Online" ? 0.3 : 100);
  const errCount   = device.events?.filter(e => e.type === "Error").length || 0;

  const primaryKey = fields[0]?.key || Object.keys(tele)[0];

  return (
    <div className="dvc-det-content">
      {/* Telemetry Cards */}
      <div className="dvc-section-title">Live Telemetry</div>
      {fields.length > 0 ? (
        <div className="dvc-tele-big-grid">
          {fields.map(f => {
            const val = tele[f.key];
            if (val === null || val === undefined) return null;
            const overHi  = f.warnHi  !== undefined && val > f.warnHi;
            const underLo = f.warnLo !== undefined && val < f.warnLo;
            const isWarn  = overHi || underLo;
            return (
              <div key={f.key} className="dvc-tele-big-card" style={{ borderTopColor: isWarn ? "#ef4444" : f.color }}>
                <div className="dvc-tele-big-val" style={{ color: isWarn ? "#ef4444" : f.color }}>{val}</div>
                <div className="dvc-tele-big-unit">{f.unit}</div>
                <div className="dvc-tele-big-lbl">{f.label}</div>
                {isWarn && <div className="dvc-tele-warn">⚠ {overHi ? `> ${f.warnHi}${f.unit}` : `< ${f.warnLo}${f.unit}`}</div>}
                {!isWarn && f.warnHi && (
                  <div style={{ marginTop: 4 }}>
                    <div style={{ width: "100%", height: 4, background: "#e5e7eb", borderRadius: 2 }}>
                      <div style={{ width: `${Math.min((val / f.warnHi) * 100, 100)}%`, height: "100%", background: f.color, borderRadius: 2 }} />
                    </div>
                  </div>
                )}
                <div style={{ fontSize: 10, color: "#9ca3af", marginTop: 4 }}>Updated {device.lastSeen.split(",")[1]?.trim() || "now"}</div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="dvc-tele-big-grid">
          {Object.entries(tele).filter(([, v]) => v !== null).map(([k, v]) => (
            <div key={k} className="dvc-tele-big-card" style={{ borderTopColor: "#667eea" }}>
              <div className="dvc-tele-big-val" style={{ color: "#667eea" }}>{v}</div>
              <div className="dvc-tele-big-lbl">{k.replace(/_/g, " ")}</div>
            </div>
          ))}
        </div>
      )}

      {/* Trend Graph */}
      <div className="dvc-section-title" style={{ marginTop: 20 }}>Telemetry Trend — {fields[0]?.label || primaryKey || "Parameter"}</div>
      <div className="dvc-trend-wrap">
        <div className="dvc-trend-controls">
          {["1H","6H","24H","7D"].map(r => (
            <button key={r} className={`dvc-view-btn ${timeRange === r ? "dvc-view-active" : ""}`} onClick={() => setTimeRange(r)}>{r}</button>
          ))}
          <button className="dvc-ctrl-btn dvc-ctrl-blue" style={{ marginLeft: "auto", padding: "4px 10px", fontSize: 12 }}>
            <FiDownload size={12} /> CSV
          </button>
        </div>
        <TrendChart device={device} timeRange={timeRange} />
        <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>
          {fields[0]?.label || "Values"} over last {timeRange} · {timeRange === "7D" ? "168" : timeRange === "24H" ? "24" : timeRange === "6H" ? "6" : "1"} data points shown
        </div>
      </div>

      {/* Quick Stats */}
      <div className="dvc-section-title" style={{ marginTop: 20 }}>Quick Stats</div>
      <div className="dvc-qs-grid">
        {[
          { label: "Uptime",           val: device.status === "Online" ? "99.2%" : device.status === "Warning" ? "94.1%" : "0%",         color: "#10b981" },
          { label: "Packets (24h)",    val: packets24h,                                                                                   color: "#3b82f6" },
          { label: "Packet Loss",      val: `${pktLoss}%`,                                                                                color: pktLoss > 5 ? "#ef4444" : "#10b981" },
          { label: "Error Count",      val: errCount,                                                                                     color: errCount > 0 ? "#f59e0b" : "#10b981" },
          { label: "Reboot Count",     val: device.events?.filter(e => e.msg.toLowerCase().includes("reboot")).length || 0,              color: "#6b7280" },
          { label: "Assigned Solution", val: device.assignedSolution || "—",                                                              color: "#667eea" },
        ].map(s => (
          <div key={s.label} className="dvc-qs-card">
            <div style={{ fontSize: 20, fontWeight: 800, color: s.color }}>{s.val}</div>
            <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 3 }}>{s.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── 3. Connectivity Tab ───────────────────────────────────────────────────────
const ConnectivityTab = ({ device }) => {
  const det = device.loraDetails || device.wifiDetails || device.ethernetDetails || device.cellularDetails || device.bluetoothDetails || device.modbusDetails || {};
  const seed = device.id.charCodeAt(0);
  const signalTrend = useMemo(() => genTrend(Math.abs(device.signal || -75), seed, 24), [device.signal, seed]);
  const maxSig = Math.max(...signalTrend.map(p => p.val), 1);

  const sections = {
    LoRa: [
      ["Gateway Connected", device.gatewayId || "Direct"],
      ["DevEUI", `${device.serialNumber?.split("-")[1] || "A8B4"}-FFFF-0000-${device.id}`],
      ["AppEUI", "7076FF000000000A"],
      ["Join Status", device.status === "Online" ? "✅ Joined (OTAA)" : "❌ Not Joined"],
      ["Uplink Count", det.packetsToday || 0],
      ["Downlink Pending", 0],
      ["RSSI", `${device.signal || "—"} dBm`],
      ["SNR", `${det.snr || "—"} dB`],
      ["Spreading Factor", det.sf || "—"],
      ["Bandwidth", det.bandwidth || "125kHz"],
      ["Coding Rate", det.codingRate || "4/5"],
      ["Packet Loss", `${det.packetLoss || 0}%`],
    ],
    WiFi: [
      ["SSID", det.ssid || "IoTMATE-Infra"],
      ["BSSID", det.bssid || "—"],
      ["IP Address", det.ip || device.ip || "—"],
      ["MAC Address", det.mac || device.mac || "—"],
      ["Channel", det.channel || "—"],
      ["Frequency", det.frequency || "2.4GHz"],
      ["RSSI", `${device.signal || "—"} dBm`],
      ["Latency", "12 ms"],
      ["Throughput", "1.2 Mbps"],
    ],
    Ethernet: [
      ["IP Address", det.ip || device.ip || "—"],
      ["MAC Address", det.mac || device.mac || "—"],
      ["Speed", det.speed || "100 Mbps"],
      ["Duplex", det.duplex || "Full"],
      ["Link State", device.status === "Online" ? "✅ Up" : "❌ Down"],
      ["Error Packets", "0"],
    ],
    Cellular: [
      ["Network", det.network || "4G LTE"],
      ["Operator", det.operator || "—"],
      ["SIM ICCID", det.simIccid || "—"],
      ["IMEI", det.imei || "—"],
      ["APN", det.apn || "—"],
      ["RSSI", `${device.signal || "—"} dBm`],
      ["Signal Bars", det.signalBars !== undefined ? `${det.signalBars}/4` : "—"],
    ],
    Bluetooth: [
      ["UUID", det.uuid || "—"],
      ["Protocol", det.protocol || "BLE 5.0"],
      ["TX Power", `${det.txPower || 0} dBm`],
      ["RSSI", `${device.signal || "—"} dBm`],
    ],
    Modbus: [
      ["Slave Address", det.slaveAddress || "—"],
      ["Protocol", det.protocol || "Modbus RTU"],
      ["Baud Rate", det.baudRate || "—"],
      ["Parity", det.parity || "None"],
      ["Stop Bits", det.stopBits || 1],
      ["Register Map", det.registerMap || "—"],
      ["Poll Interval", "5 sec"],
      ["Last Poll", device.lastSeen],
      ["Poll Status", device.status === "Online" ? "✅ OK" : "❌ Failed"],
    ],
  }[device.connectivity] || [];

  return (
    <div className="dvc-det-content">
      <div className="dvc-section-title">Protocol Details — {device.connectivity}</div>
      <div className="dvc-info-grid" style={{ marginTop: 10 }}>
        {sections.map(([label, val]) => (
          <div key={label} className="dvc-info-row">
            <div className="dvc-info-lbl">{label}</div>
            <div className="dvc-info-val" style={{ fontFamily: ["SIM ICCID","IMEI","DevEUI","AppEUI","MAC Address","BSSID"].includes(label) ? "monospace" : "inherit", fontSize: 12 }}>{String(val)}</div>
          </div>
        ))}
      </div>

      {device.signal && (
        <>
          <div className="dvc-section-title" style={{ marginTop: 20 }}>Signal Strength Trend (24h)</div>
          <div className="dvc-trend-wrap" style={{ marginTop: 8 }}>
            <div style={{ display: "flex", alignItems: "flex-end", gap: 3, height: 70 }}>
              {signalTrend.map((pt, i) => (
                <div key={i} style={{ flex: 1, height: `${(pt.val / maxSig) * 64}px`, background: pt.val / maxSig > 0.7 ? "#10b981" : pt.val / maxSig > 0.5 ? "#f59e0b" : "#ef4444", borderRadius: "3px 3px 0 0", minHeight: 2, opacity: 0.8 }} />
              ))}
            </div>
            <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 6 }}>Signal RSSI trend — current: {device.signal} dBm · Gateway: {device.gatewayId || "Direct"}</div>
          </div>
        </>
      )}

      <div style={{ marginTop: 12, padding: 10, background: "#f0fdf4", borderRadius: 8, fontSize: 12, color: "#374151" }}>
        🤖 AI: {device.signal && device.signal < -85 ? "Weak signal detected. Check antenna orientation and reduce physical obstructions between device and gateway." : "Connectivity is stable. All parameters within acceptable range."}
      </div>
    </div>
  );
};

// ── 4. Settings Tab ───────────────────────────────────────────────────────────
const SettingsTab = ({ device }) => {
  const [section, setSection] = useState("General");
  const fields = TELE_MAP[device.type] || Object.keys(device.telemetry || {}).map(k => ({ key: k, label: k, unit: "" }));
  const [thresholds, setThresholds] = useState(
    Object.fromEntries(fields.map(f => [f.key, { min: f.warnLo || 0, max: f.warnHi || 100, alert: true, delay: 30 }]))
  );

  return (
    <div className="dvc-det-content">
      <div className="dvc-settings-nav">
        {["General","Reporting","Thresholds","Control","Network"].map(s => (
          <button key={s} className={`dvc-view-btn ${section === s ? "dvc-view-active" : ""}`} onClick={() => setSection(s)}>{s}</button>
        ))}
      </div>

      {section === "General" && (
        <div className="dvc-settings-form">
          <div className="dvc-section-title">General Settings</div>
          {[
            { label: "Device Name",          val: device.name,              type: "input" },
            { label: "Location",             val: device.site,              type: "input" },
            { label: "Site",                 val: device.site,              type: "input" },
            { label: "Assigned Solution",    val: device.assignedSolution,  type: "select", options: ["STP","AQM","Irrigation","Facility","None"] },
            { label: "Timezone",             val: "Asia/Kolkata",           type: "select", options: ["Asia/Kolkata","Asia/Dubai","UTC","Europe/London"] },
            { label: "Description",          val: `${device.type} at ${device.site}`, type: "textarea" },
          ].map(f => (
            <div key={f.label} className="dvc-sf-row">
              <label className="dvc-sf-label">{f.label}</label>
              {f.type === "textarea" ? (
                <textarea className="dvc-sf-input dvc-sf-textarea" defaultValue={f.val} rows={3} />
              ) : f.type === "select" ? (
                <select className="dvc-sf-input" defaultValue={f.val}>
                  {f.options?.map(o => <option key={o}>{o}</option>)}
                </select>
              ) : (
                <input className="dvc-sf-input" type="text" defaultValue={f.val} />
              )}
            </div>
          ))}
          <button className="dvc-ctrl-btn dvc-ctrl-green" style={{ marginTop: 12, gap: 5 }}><FiSave size={13} /> Save Changes</button>
        </div>
      )}

      {section === "Reporting" && (
        <div className="dvc-settings-form">
          <div className="dvc-section-title">Reporting Settings</div>
          {[
            { label: "Reporting Interval",  val: "5 min",   type: "select", options: ["30 sec","1 min","5 min","15 min","30 min","1 hr"] },
            { label: "Heartbeat Interval",  val: "1 min",   type: "select", options: ["30 sec","1 min","5 min","10 min","15 min"] },
            { label: "Retry Attempts",       val: "3",       type: "number" },
            { label: "Sleep Mode",           val: "Off",     type: "select", options: ["Off","Light Sleep","Deep Sleep"] },
            { label: "Wake Duration",        val: "30 sec",  type: "select", options: ["10 sec","30 sec","1 min","5 min"] },
            { label: "Downlink ACK",         val: "Enabled", type: "select", options: ["Enabled","Disabled"] },
          ].map(f => (
            <div key={f.label} className="dvc-sf-row">
              <label className="dvc-sf-label">{f.label}</label>
              {f.type === "select" ? (
                <select className="dvc-sf-input" defaultValue={f.val}>
                  {f.options?.map(o => <option key={o}>{o}</option>)}
                </select>
              ) : (
                <input className="dvc-sf-input" type={f.type} defaultValue={f.val} />
              )}
            </div>
          ))}
          <button className="dvc-ctrl-btn dvc-ctrl-green" style={{ marginTop: 12, gap: 5 }}><FiSave size={13} /> Save Changes</button>
        </div>
      )}

      {section === "Thresholds" && (
        <div className="dvc-settings-form">
          <div className="dvc-section-title">Threshold Alerts</div>
          <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 10 }}>Configure per-parameter alert thresholds. Alerts trigger when a reading exceeds the defined range.</div>
          {fields.length === 0 && <div className="dvc-empty">No threshold parameters available for this device type.</div>}
          {fields.map(f => (
            <div key={f.key} className="dvc-threshold-row">
              <div style={{ fontWeight: 700, fontSize: 13, color: "#374151", width: 120 }}>{f.label} {f.unit && `(${f.unit})`}</div>
              <label style={{ fontSize: 12, color: "#6b7280" }}>Min</label>
              <input className="dvc-sf-input" style={{ width: 70 }} type="number" defaultValue={thresholds[f.key]?.min} />
              <label style={{ fontSize: 12, color: "#6b7280" }}>Max</label>
              <input className="dvc-sf-input" style={{ width: 70 }} type="number" defaultValue={thresholds[f.key]?.max} />
              <label style={{ fontSize: 12, color: "#6b7280" }}>Delay (s)</label>
              <input className="dvc-sf-input" style={{ width: 60 }} type="number" defaultValue={thresholds[f.key]?.delay} />
              <label style={{ display: "flex", alignItems: "center", gap: 5, cursor: "pointer", fontSize: 12, color: "#374151" }}>
                <input type="checkbox" defaultChecked={thresholds[f.key]?.alert} /> Alert
              </label>
            </div>
          ))}
          <button className="dvc-ctrl-btn dvc-ctrl-green" style={{ marginTop: 12, gap: 5 }}><FiSave size={13} /> Save Thresholds</button>
        </div>
      )}

      {section === "Control" && (
        <div className="dvc-settings-form">
          <div className="dvc-section-title">Control Settings</div>
          {device.controls ? (
            <>
              {[
                { label: "Operating Mode",    val: device.controls.mode || "Auto",    type: "select", options: ["Auto","Manual","Schedule","Remote"] },
                { label: "Default Safe State", val: "Closed",   type: "select", options: ["Open","Closed","Last Known"] },
                { label: "Setpoint",          val: device.controls.speedSetpoint || device.controls.setpoint || 0, type: "number" },
                { label: "Fail-safe Action",  val: "Shutdown",  type: "select", options: ["Shutdown","Hold Last","Open","Close"] },
                { label: "Schedule",          val: device.controls.schedule || "—",   type: "input" },
              ].map(f => (
                <div key={f.label} className="dvc-sf-row">
                  <label className="dvc-sf-label">{f.label}</label>
                  {f.type === "select" ? (
                    <select className="dvc-sf-input" defaultValue={f.val}>
                      {f.options?.map(o => <option key={o}>{o}</option>)}
                    </select>
                  ) : (
                    <input className="dvc-sf-input" type={f.type} defaultValue={f.val} />
                  )}
                </div>
              ))}
              <button className="dvc-ctrl-btn dvc-ctrl-green" style={{ marginTop: 12, gap: 5 }}><FiSave size={13} /> Save Control Config</button>
            </>
          ) : (
            <div className="dvc-empty">This device type does not support control configuration.</div>
          )}
        </div>
      )}

      {section === "Network" && (
        <div className="dvc-settings-form">
          <div className="dvc-section-title">Network Settings — {device.connectivity}</div>
          {device.connectivity === "LoRa" && [
            { label: "DevEUI",         val: `${device.serialNumber}-FFFF`, type: "input" },
            { label: "AppEUI",         val: "7076FF000000000A",             type: "input" },
            { label: "AppKey",         val: "••••••••••••••••••••••••",     type: "password" },
            { label: "Join Type",      val: "OTAA",                         type: "select", options: ["OTAA","ABP"] },
            { label: "Spreading Factor", val: String(device.loraDetails?.sf || 10), type: "select", options: ["7","8","9","10","11","12"] },
            { label: "Bandwidth",      val: "125kHz",                       type: "select", options: ["125kHz","250kHz","500kHz"] },
          ].map(f => (
            <div key={f.label} className="dvc-sf-row">
              <label className="dvc-sf-label">{f.label}</label>
              {f.type === "select" ? (
                <select className="dvc-sf-input" defaultValue={f.val}>{f.options?.map(o => <option key={o}>{o}</option>)}</select>
              ) : (
                <input className="dvc-sf-input" type={f.type === "password" ? "password" : "text"} defaultValue={f.val} />
              )}
            </div>
          ))}
          {device.connectivity === "WiFi" && [
            { label: "SSID",       val: device.wifiDetails?.ssid || "",    type: "input" },
            { label: "Password",   val: "••••••••",                         type: "password" },
            { label: "IP Mode",    val: "DHCP",                             type: "select", options: ["DHCP","Static"] },
            { label: "IP Address", val: device.wifiDetails?.ip || "",      type: "input" },
            { label: "DNS",        val: "8.8.8.8",                          type: "input" },
          ].map(f => (
            <div key={f.label} className="dvc-sf-row">
              <label className="dvc-sf-label">{f.label}</label>
              {f.type === "select" ? (
                <select className="dvc-sf-input" defaultValue={f.val}>{f.options?.map(o => <option key={o}>{o}</option>)}</select>
              ) : (
                <input className="dvc-sf-input" type={f.type === "password" ? "password" : "text"} defaultValue={f.val} />
              )}
            </div>
          ))}
          {device.connectivity === "Cellular" && [
            { label: "APN",        val: device.cellularDetails?.apn || "",  type: "input" },
            { label: "Username",   val: "",                                  type: "input" },
            { label: "Password",   val: "••••••",                            type: "password" },
            { label: "Band",       val: "Auto",                              type: "select", options: ["Auto","B1","B3","B5","B8","B40"] },
          ].map(f => (
            <div key={f.label} className="dvc-sf-row">
              <label className="dvc-sf-label">{f.label}</label>
              {f.type === "select" ? (
                <select className="dvc-sf-input" defaultValue={f.val}>{f.options?.map(o => <option key={o}>{o}</option>)}</select>
              ) : (
                <input className="dvc-sf-input" type={f.type === "password" ? "password" : "text"} defaultValue={f.val} />
              )}
            </div>
          ))}
          {device.connectivity === "Modbus" && [
            { label: "Slave Address", val: String(device.modbusDetails?.slaveAddress || 1), type: "number" },
            { label: "Baud Rate",     val: String(device.modbusDetails?.baudRate || 9600),  type: "select", options: ["2400","4800","9600","19200","38400","115200"] },
            { label: "Parity",        val: device.modbusDetails?.parity || "None",          type: "select", options: ["None","Even","Odd"] },
            { label: "Stop Bits",     val: String(device.modbusDetails?.stopBits || 1),     type: "select", options: ["1","2"] },
            { label: "Poll Interval", val: "5",                                              type: "number" },
            { label: "Register Map",  val: device.modbusDetails?.registerMap || "",         type: "input" },
          ].map(f => (
            <div key={f.label} className="dvc-sf-row">
              <label className="dvc-sf-label">{f.label}</label>
              {f.type === "select" ? (
                <select className="dvc-sf-input" defaultValue={f.val}>{f.options?.map(o => <option key={o}>{o}</option>)}</select>
              ) : (
                <input className="dvc-sf-input" type={f.type} defaultValue={f.val} />
              )}
            </div>
          ))}
          {["Ethernet","Bluetooth"].includes(device.connectivity) && (
            <div className="dvc-empty">Network settings for {device.connectivity} are managed automatically.</div>
          )}
          <button className="dvc-ctrl-btn dvc-ctrl-green" style={{ marginTop: 12, gap: 5 }}><FiSave size={13} /> Apply Network Settings</button>
        </div>
      )}
    </div>
  );
};

// ── 5. Control Tab ────────────────────────────────────────────────────────────
const ControlTab = ({ device }) => {
  const [cmdLog, setCmdLog] = useState([
    { cmd: "Status Query", status: "Acknowledged", time: device.lastSeen, result: "OK" },
    { cmd: "Heartbeat",    status: "Delivered",    time: device.lastSeen, result: "Alive" },
  ]);
  const [cmdStatus, setCmdStatus] = useState(null);

  const sendCmd = (cmd) => {
    setCmdStatus("Sending…");
    setTimeout(() => setCmdStatus("Delivered"), 600);
    setTimeout(() => setCmdStatus("Acknowledged ✓"), 1400);
    setTimeout(() => { setCmdStatus(null); setCmdLog(prev => [{ cmd, status: "Acknowledged", time: new Date().toLocaleTimeString(), result: "OK" }, ...prev.slice(0, 9)]); }, 2000);
  };

  if (device.role === "Sensor") {
    return (
      <div className="dvc-det-content">
        <div className="dvc-empty" style={{ padding: 30 }}>
          <FiActivity size={32} color="#d1d5db" style={{ marginBottom: 8 }} />
          <div style={{ fontSize: 14, fontWeight: 600, color: "#9ca3af" }}>Read-only monitoring device</div>
          <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 4 }}>This sensor does not support direct control commands. Use the Settings tab to configure thresholds.</div>
        </div>
        <div className="dvc-section-title" style={{ marginTop: 16 }}>Diagnostic Commands</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
          {["Ping", "Restart", "Calibrate", "Force Report"].map(c => (
            <button key={c} className="dvc-ctrl-btn dvc-ctrl-blue" onClick={() => sendCmd(c)}><FiZap size={12} /> {c}</button>
          ))}
        </div>
        {cmdStatus && <div style={{ marginTop: 10, padding: 8, background: "#f0fdf4", borderRadius: 8, fontSize: 13, color: "#10b981", fontWeight: 600 }}>{cmdStatus}</div>}
      </div>
    );
  }

  const ctrls = device.controls || {};
  return (
    <div className="dvc-det-content">
      <div className="dvc-ctrl-panel">
        {/* Mode & Status */}
        <div className="dvc-ctrl-section">
          <div className="dvc-section-title">Mode</div>
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            {["Auto","Manual","Schedule"].map(m => (
              <button key={m} className={`dvc-ctrl-btn ${ctrls.mode === m ? "dvc-ctrl-purple" : ""}`}
                style={ctrls.mode !== m ? { background: "#f1f5f9", color: "#374151" } : {}}
                onClick={() => sendCmd(`Set Mode: ${m}`)}>
                {m}
              </button>
            ))}
          </div>
        </div>

        {/* Device-type specific controls */}
        {device.type === "Irrigation Controller" && (
          <div className="dvc-ctrl-section">
            <div className="dvc-section-title">Valve Control</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
              {(ctrls.valves || ["Zone1:Closed","Zone2:Closed","Zone3:Closed","Zone4:Closed"]).map((v, i) => {
                const [zone, state] = v.split(":");
                return (
                  <div key={i} className="dvc-valve-card" style={{ borderColor: state === "Open" ? "#10b981" : "#e5e7eb" }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#374151" }}>{zone}</div>
                    <button className={`dvc-ctrl-btn ${state === "Open" ? "dvc-ctrl-green" : ""}`}
                      style={{ fontSize: 11, padding: "4px 10px", marginTop: 6, background: state === "Open" ? undefined : "#fee2e2", color: state === "Open" ? undefined : "#ef4444", borderColor: state === "Open" ? undefined : "#fca5a5" }}
                      onClick={() => sendCmd(`${zone} ${state === "Open" ? "Close" : "Open"}`)}>
                      {state}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {(device.type === "Pump Controller" || device.type === "HVAC Controller") && (
          <div className="dvc-ctrl-section">
            <div className="dvc-section-title">Primary Control</div>
            <div style={{ display: "flex", gap: 10, marginTop: 8, flexWrap: "wrap", alignItems: "center" }}>
              <button className="dvc-ctrl-btn dvc-ctrl-green" onClick={() => sendCmd("Start")}><FiZap size={13} /> Start</button>
              <button className="dvc-ctrl-btn dvc-ctrl-red"   onClick={() => sendCmd("Stop")}><FiX size={13} /> Stop</button>
              {device.type === "HVAC Controller" && (
                <>
                  <label style={{ fontSize: 12, color: "#6b7280" }}>Setpoint</label>
                  <input className="dvc-sf-input" type="number" defaultValue={ctrls.setpoint || 24} style={{ width: 70 }} />
                  <span style={{ fontSize: 12, color: "#6b7280" }}>°C</span>
                </>
              )}
            </div>
          </div>
        )}

        {device.type === "Motor Drive" && (
          <div className="dvc-ctrl-section">
            <div className="dvc-section-title">VFD Control</div>
            <div style={{ display: "flex", gap: 10, marginTop: 8, alignItems: "center", flexWrap: "wrap" }}>
              <label style={{ fontSize: 12, color: "#6b7280" }}>Speed Setpoint</label>
              <input className="dvc-sf-input" type="number" defaultValue={ctrls.speedSetpoint || 1450} style={{ width: 90 }} />
              <span style={{ fontSize: 12, color: "#6b7280" }}>RPM</span>
              <button className="dvc-ctrl-btn dvc-ctrl-green" onClick={() => sendCmd("Apply Speed")}>Apply</button>
              <button className="dvc-ctrl-btn dvc-ctrl-blue" onClick={() => sendCmd("Direction Reverse")}>Reverse</button>
            </div>
          </div>
        )}

        {/* Command status */}
        {cmdStatus && (
          <div style={{ padding: 10, background: "#f0fdf4", borderRadius: 8, fontSize: 13, color: "#10b981", fontWeight: 600, marginTop: 10 }}>
            Command {cmdStatus}
          </div>
        )}

        {/* Common actions */}
        <div className="dvc-ctrl-section">
          <div className="dvc-section-title">Actions</div>
          <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
            {["Ping","Restart","Force Report","Sync Time"].map(c => (
              <button key={c} className="dvc-ctrl-btn dvc-ctrl-blue" onClick={() => sendCmd(c)}><FiZap size={12} /> {c}</button>
            ))}
          </div>
        </div>

        {/* Command log */}
        <div className="dvc-section-title" style={{ marginTop: 16 }}>Command Log</div>
        <div className="dvc-cmd-log">
          {cmdLog.map((c, i) => (
            <div key={i} className="dvc-cmd-row">
              <span style={{ fontSize: 12, color: "#374151", fontWeight: 600 }}>{c.cmd}</span>
              <span style={{ padding: "1px 7px", borderRadius: 10, background: c.status === "Acknowledged" ? "#d1fae5" : "#fef3c7", color: c.status === "Acknowledged" ? "#059669" : "#92400e", fontSize: 11, fontWeight: 700 }}>{c.status}</span>
              <span style={{ fontSize: 11, color: "#9ca3af", marginLeft: "auto" }}>{c.time}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ── 6. Health & Diagnostics Tab ───────────────────────────────────────────────
const HealthTab = ({ device }) => {
  const h = device.healthScore;
  const seed = device.id.charCodeAt(1) || 65;
  const cpuUsage  = Math.round(30 + seededRnd(seed, 1) * 50);
  const memUsage  = Math.round(40 + seededRnd(seed, 2) * 40);
  const intTemp   = +(28 + seededRnd(seed, 3) * 20).toFixed(1);
  const voltage   = +(3.2 + seededRnd(seed, 4) * 0.8).toFixed(2);
  const errCount  = device.events?.filter(e => e.type === "Error").length || 0;

  const metrics = [
    { label: "CPU Usage",       val: `${cpuUsage}%`,   color: cpuUsage > 80 ? "#ef4444" : "#10b981",  bar: cpuUsage },
    { label: "Memory Usage",    val: `${memUsage}%`,   color: memUsage > 85 ? "#ef4444" : "#10b981",  bar: memUsage },
    { label: "Internal Temp",   val: `${intTemp}°C`,   color: intTemp > 45 ? "#ef4444" : "#10b981",   bar: (intTemp / 80) * 100 },
    { label: "Supply Voltage",  val: `${voltage}V`,    color: voltage < 3.3 ? "#f59e0b" : "#10b981",  bar: (voltage / 4.2) * 100 },
    { label: "Error Count",     val: errCount,         color: errCount > 0 ? "#f59e0b" : "#10b981",   bar: Math.min(errCount * 10, 100) },
    { label: "Health Score",    val: h,                color: healthColor(h),                          bar: h },
  ];

  return (
    <div className="dvc-det-content">
      <div className="dvc-section-title">System Metrics</div>
      <div className="dvc-health-metrics" style={{ marginTop: 10 }}>
        {metrics.map(m => (
          <div key={m.label} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            <div style={{ width: 120, fontSize: 12, fontWeight: 500, color: "#374151" }}>{m.label}</div>
            <div style={{ flex: 1, height: 10, background: "#e5e7eb", borderRadius: 5, overflow: "hidden" }}>
              <div style={{ width: `${m.bar}%`, height: "100%", background: m.color, borderRadius: 5 }} />
            </div>
            <div style={{ width: 50, textAlign: "right", fontSize: 13, fontWeight: 700, color: m.color }}>{m.val}</div>
          </div>
        ))}
      </div>

      <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
        <button className="dvc-ctrl-btn dvc-ctrl-blue"><FiShield size={13} /> Run Self-Diagnostic</button>
        <button className="dvc-ctrl-btn dvc-ctrl-purple"><FiDownload size={13} /> Download Log File</button>
      </div>

      <div className="dvc-section-title" style={{ marginTop: 20 }}>Error Log</div>
      <div className="dvc-cmd-log" style={{ marginTop: 8 }}>
        {(device.events || []).filter(e => e.type === "Error" || e.type === "Warning").map((e, i) => (
          <div key={i} className="dvc-cmd-row">
            <span style={{ padding: "1px 7px", borderRadius: 10, background: e.type === "Error" ? "#fee2e2" : "#fef3c7", color: e.type === "Error" ? "#ef4444" : "#f59e0b", fontSize: 11, fontWeight: 700 }}>{e.type}</span>
            <span style={{ fontSize: 12, color: "#374151", flex: 1 }}>{e.msg}</span>
            <span style={{ fontSize: 11, color: "#9ca3af" }}>{e.time}</span>
          </div>
        ))}
        {(device.events || []).filter(e => e.type === "Error" || e.type === "Warning").length === 0 && (
          <div className="dvc-empty">No errors or warnings recorded ✅</div>
        )}
      </div>

      <div className="dvc-section-title" style={{ marginTop: 20 }}>Restart History</div>
      <div className="dvc-cmd-log" style={{ marginTop: 8 }}>
        {[
          { reason: "Manual reboot", time: "2026-02-25 09:12" },
          { reason: "OTA firmware update", time: "2026-02-10 03:00" },
          { reason: "Power cycle", time: "2026-01-28 17:44" },
        ].map((r, i) => (
          <div key={i} className="dvc-cmd-row">
            <span style={{ fontSize: 12, color: "#374151" }}>{r.reason}</span>
            <span style={{ fontSize: 11, color: "#9ca3af", marginLeft: "auto" }}>{r.time}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── 7. Events Tab ─────────────────────────────────────────────────────────────
const EventsTab = ({ device }) => (
  <div className="dvc-det-content">
    <div className="dvc-section-title">Activity Timeline</div>
    <div style={{ marginTop: 10 }}>
      {(device.events || []).length === 0 && <div className="dvc-empty">No events recorded</div>}
      {(device.events || []).map((ev, i) => (
        <div key={i} className="dvc-ev-entry" style={{ paddingBottom: 14 }}>
          <div className="dvc-ev-timeline-col">
            <div className="dvc-ev-dot-lg" style={{ background: ev.type === "Error" ? "#ef4444" : ev.type === "Warning" ? "#f59e0b" : "#10b981" }} />
            {i < (device.events || []).length - 1 && <div className="dvc-ev-line" />}
          </div>
          <div style={{ flex: 1, paddingBottom: 4 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ padding: "1px 7px", borderRadius: 10, background: ev.type === "Error" ? "#fee2e2" : ev.type === "Warning" ? "#fef3c7" : "#d1fae5", color: ev.type === "Error" ? "#ef4444" : ev.type === "Warning" ? "#f59e0b" : "#10b981", fontSize: 11, fontWeight: 700 }}>{ev.type}</span>
              <span style={{ fontSize: 11, color: "#9ca3af" }}>{ev.time}</span>
              <span style={{ fontSize: 11, color: "#6b7280", marginLeft: "auto" }}>by System</span>
            </div>
            <div style={{ fontSize: 13, color: "#374151", marginTop: 4 }}>{ev.msg}</div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

// ── 8. Firmware Tab ───────────────────────────────────────────────────────────
const FirmwareTab = ({ device }) => {
  const curVer = device.firmware;
  const parts  = curVer.replace("v","").split(".");
  const newVer = `v${parts[0]}.${parts[1]}.${+parts[2] + 1 || 0}`;

  return (
    <div className="dvc-det-content">
      <div className="dvc-fw-cards">
        <div className="dvc-fw-card" style={{ borderTopColor: "#10b981" }}>
          <div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 600, textTransform: "uppercase" }}>Current Firmware</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: "#10b981", marginTop: 4 }}>{curVer}</div>
          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>Installed on {device.installedOn}</div>
          <div style={{ padding: "4px 10px", borderRadius: 10, background: "#d1fae5", color: "#059669", fontSize: 11, fontWeight: 700, display: "inline-block", marginTop: 8 }}>✅ Up to Date</div>
        </div>
        <div className="dvc-fw-card" style={{ borderTopColor: "#667eea" }}>
          <div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 600, textTransform: "uppercase" }}>Available Update</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: "#667eea", marginTop: 4 }}>{newVer}</div>
          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>Release: 2026-02-15 · Size: 184 KB</div>
          <button className="dvc-ctrl-btn dvc-ctrl-purple" style={{ marginTop: 8, gap: 5 }}><FiDownload size={13} /> Install Update</button>
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
        <button className="dvc-ctrl-btn dvc-ctrl-orange"><FiRefreshCw size={13} /> Rollback to Previous</button>
        <button className="dvc-ctrl-btn dvc-ctrl-blue"><FiClock size={13} /> Schedule OTA</button>
      </div>

      <div className="dvc-section-title" style={{ marginTop: 20 }}>Upgrade History</div>
      <div className="dvc-cmd-log" style={{ marginTop: 8 }}>
        {[
          { ver: curVer,    date: device.installedOn,  result: "✅ Success", notes: "Bug fixes and stability improvements" },
          { ver: "v2.3.0",  date: "2025-11-01",        result: "✅ Success", notes: "Added LoRa ADR support" },
          { ver: "v2.2.5",  date: "2025-08-14",        result: "❌ Failed",  notes: "OTA interrupted — power loss" },
          { ver: "v2.2.5",  date: "2025-08-15",        result: "✅ Success", notes: "Retry after power restore" },
        ].map((h, i) => (
          <div key={i} className="dvc-cmd-row">
            <span style={{ fontSize: 13, fontWeight: 700, color: "#374151", minWidth: 60 }}>{h.ver}</span>
            <span style={{ fontSize: 12, color: "#6b7280", flex: 1 }}>{h.notes}</span>
            <span style={{ fontSize: 11, color: "#9ca3af" }}>{h.date}</span>
            <span style={{ fontSize: 11, fontWeight: 600, color: h.result.includes("✅") ? "#10b981" : "#ef4444" }}>{h.result}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── 9. Relationships Tab ──────────────────────────────────────────────────────
const RelationshipsTab = ({ device }) => {
  const { statusMeta: sm2, connMeta: cm2, healthColor: hc2, HealthRing: HR2 } = { statusMeta, connMeta, healthColor, HealthRing };

  if (device.role === "Gateway") {
    const children = (device.childDeviceIds || []).map(id => allDevices.find(x => x.id === id)).filter(Boolean);
    return (
      <div className="dvc-det-content">
        <div className="dvc-section-title">Connected Child Devices ({children.length})</div>
        <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 8 }}>
          {children.map(child => {
            const csm = sm2(child.status);
            const ccm = cm2(child.connectivity);
            const chc = hc2(child.healthScore);
            return (
              <div key={child.id} className="dvc-rel-child">
                <HealthRing score={child.healthScore} size={40} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: "#1e293b" }}>{child.name}</div>
                  <div style={{ fontSize: 11, color: "#9ca3af", fontFamily: "monospace" }}>{child.id} · {child.type}</div>
                  <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
                    <span style={{ padding: "1px 7px", borderRadius: 10, background: csm.bg, color: csm.color, fontSize: 11, fontWeight: 700 }}>{child.status}</span>
                    <span style={{ padding: "1px 7px", borderRadius: 10, background: ccm.bg, color: ccm.color, fontSize: 11 }}>{child.connectivity}</span>
                    {child.signal && <span style={{ fontSize: 11, color: "#6b7280" }}>{child.signal} dBm</span>}
                    {child.battery !== null && child.battery !== undefined && <span style={{ fontSize: 11, color: child.battery < 20 ? "#ef4444" : "#6b7280" }}>🔋{child.battery}%</span>}
                  </div>
                </div>
                <div style={{ fontSize: 11, color: "#9ca3af" }}>{child.lastSeen}</div>
              </div>
            );
          })}
          {children.length === 0 && <div className="dvc-empty">No child devices registered under this gateway</div>}
        </div>
      </div>
    );
  }

  // Leaf device — show parent gateway
  const parent = device.gatewayId ? allDevices.find(x => x.id === device.gatewayId) : null;
  return (
    <div className="dvc-det-content">
      <div className="dvc-section-title">Parent Gateway</div>
      {parent ? (
        <div className="dvc-rel-child" style={{ marginTop: 10, borderColor: "#667eea", background: "#f5f3ff" }}>
          <HealthRing score={parent.healthScore} size={48} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: "#1e293b" }}>{parent.name}</div>
            <div style={{ fontSize: 11, color: "#9ca3af", fontFamily: "monospace" }}>{parent.id} · {parent.connectivity} Gateway</div>
            <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
              {(() => { const pm = statusMeta(parent.status); return <span style={{ padding: "1px 7px", borderRadius: 10, background: pm.bg, color: pm.color, fontSize: 11, fontWeight: 700 }}>{parent.status}</span>; })()}
              <span style={{ fontSize: 11, color: "#6b7280" }}>{parent.site}</span>
            </div>
          </div>
        </div>
      ) : (
        <div className="dvc-empty" style={{ marginTop: 10 }}>This device is connected directly (no gateway)</div>
      )}

      <div className="dvc-section-title" style={{ marginTop: 20 }}>Connection History</div>
      <div className="dvc-cmd-log" style={{ marginTop: 8 }}>
        {[
          { event: "Joined gateway", gw: device.gatewayId || "Direct", time: device.installedOn },
          { event: "Re-joined after signal loss", gw: device.gatewayId || "Direct", time: "2026-01-15" },
          { event: "Initial registration", gw: device.gatewayId || "Network", time: device.installedOn },
        ].map((r, i) => (
          <div key={i} className="dvc-cmd-row">
            <span style={{ fontSize: 12, color: "#374151" }}>{r.event}</span>
            <span style={{ fontSize: 11, color: "#6b7280" }}>via {r.gw}</span>
            <span style={{ fontSize: 11, color: "#9ca3af", marginLeft: "auto" }}>{r.time}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────
const DETAIL_TABS = [
  { id: "overview",      label: "Overview"      },
  { id: "connectivity",  label: "Connectivity"  },
  { id: "settings",      label: "Settings"      },
  { id: "control",       label: "Control"       },
  { id: "health",        label: "Health & Diag" },
  { id: "events",        label: "Events"        },
  { id: "firmware",      label: "Firmware"      },
  { id: "relationships", label: "Relationships" },
];

const DvcDeviceDetailPanel = ({ device, onSelectDevice }) => {
  const [tab, setTab] = useState("overview");

  if (!device) {
    return (
      <div className="dvc-no-device">
        <div style={{ fontSize: 50 }}>📡</div>
        <div style={{ fontSize: 16, fontWeight: 700, color: "#374151", marginTop: 12 }}>No device selected</div>
        <div style={{ fontSize: 13, color: "#9ca3af", marginTop: 6 }}>Select a device from the Devices or Topology tab to view its full details here.</div>
      </div>
    );
  }

  return (
    <div className="dvc-device-detail-page">
      <DeviceIdentityBar
        device={device}
        onEdit={() => alert(`Editing ${device.name}`)}
        onReboot={() => alert(`Rebooting ${device.name}…`)}
        onFirmware={() => setTab("firmware")}
        onDelete={() => alert(`Delete ${device.name}?`)}
      />
      <div className="dvc-det-tab-bar">
        {DETAIL_TABS.map(t => (
          <button key={t.id} className={`dvc-det-tab ${tab === t.id ? "dvc-det-tab-active" : ""}`} onClick={() => setTab(t.id)}>
            {t.label}
          </button>
        ))}
      </div>
      {tab === "overview"      && <OverviewTab      device={device} />}
      {tab === "connectivity"  && <ConnectivityTab  device={device} />}
      {tab === "settings"      && <SettingsTab      device={device} />}
      {tab === "control"       && <ControlTab       device={device} />}
      {tab === "health"        && <HealthTab        device={device} />}
      {tab === "events"        && <EventsTab        device={device} />}
      {tab === "firmware"      && <FirmwareTab      device={device} />}
      {tab === "relationships" && <RelationshipsTab device={device} />}
    </div>
  );
};

export default DvcDeviceDetailPanel;
